/**
 * Acceptation d'une invitation — route `/invitation/:token`, HORS `AppShell` : comme
 * `/partage/:token` (`SharedProjectPage`), l'invité consulte l'aperçu **avant même d'être
 * connecté** — cette page vit donc en dehors du garde d'authentification dans `App.jsx`.
 *
 * L'aperçu (`GET /api/invitations/:token`, public) rend 404 aussi bien pour un jeton révoqué
 * qu'expiré : le serveur ne distingue pas les deux, pour ne pas confirmer qu'un jeton a
 * existé (§10 de la spec). Le message reste donc volontairement générique côté client aussi,
 * mais explicite sur la marche à suivre — jamais un « introuvable » brut. L'appel ne porte
 * jamais de jeton (`{ auth: false }`, D5) : un jeton expiré resté dans `sessionStorage` ne
 * doit jamais faire échouer cet aperçu public.
 *
 * Deux routes pour un même jeton (spec onboarding fluide, D-C1) : l'aperçu public, qui ne change
 * pas, et `GET /invitations/:token/statut`, réservée aux personnes connectées. Quand l'aperçu
 * répond 404 et que la personne est connectée, on demande le statut : 200 prouve que c'est
 * elle qui a accepté cette invitation → écran « vous avez déjà rejoint X » + « Ouvrir »,
 * jamais un message d'expiration trompeur. L'aperçu reste `{ auth: false }` — c'est ce qui
 * garantit qu'un jeton périmé ne le casse pas ; le statut, lui, porte le jeton.
 *
 * Après acceptation, la personne arrive seule dans l'app : un court message de succès, puis
 * redirection vers la destination renvoyée par le serveur (`lib/invitation-destination.js`).
 *
 * `startLogin(returnTo)` (lot 2, mode CIAM) mémorise `/invitation/:token` dans le `state`
 * MSAL — le retour de connexion (`SessionContext`) navigue dessus. Hors configuration CIAM
 * (D7, repli jusqu'au lot 3), `startLegacyLogin(provider, returnTo)` fait de même via le flux
 * OAuth serveur, comme avant ce lot. `POST accept` n'est déclenché qu'une fois
 * `status === 'authenticated'` : c'est ce statut qui garantit que le rôle
 * `exiplan.utilisateur` est bien dans le jeton avant que cette page n'agisse (sinon
 * `status` vaudrait `activating`/`suspended`, gérés par `ActivationPage`, jamais ici).
 *
 * Mode `registre` (lot O3, Exidis/exiPlan#168) : `AcceptationRegistre`. Pas d'aperçu public — le
 * jeton ne doit JAMAIS figurer dans une URL de requête API, il finirait dans les journaux d'accès.
 * Il reste dans l'URL de la PAGE (`/invitation/:token`, lue ici) et voyage dans le CORPS d'un
 * `POST /api/invitations/accepter { jeton }`. Sans aperçu, l'écran ne connaît ni l'organisation ni
 * l'invitant avant l'acceptation : il le dit sans les inventer. Le mode vient de la session
 * (`organisationStudio`, D-J), jamais d'une variable `VITE_*`.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../contexts/SessionContext.jsx';
import { api } from '../lib/api.js';
import { ciamConfigured } from '../lib/auth.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { cheminDeDestination } from '../lib/invitation-destination.js';
import { messageInvitation } from '../lib/invitation-errors.js';
import { fournisseurPrincipal, startLegacyLogin, startLogin } from '../lib/connexion.js';
import { useAsync } from '../hooks/useAsync.js';
import { Empty, PrimaryButton } from '../components/common.jsx';
import { RoleBadge } from '../components/RoleBadge.jsx';
import MenuLangue from '../components/common/MenuLangue.jsx';
import { BRAND, BRAND_GLYPH, BTN_DARK, BTN_GHOST, EYEBROW, PAGE_TITLE } from '../ui/theme.js';
import GlypheExiPlan from '../components/common/GlypheExiPlan.jsx';

/** Littéraux dans l'objet — le gate `keys.test.js` ne voit pas une clé de gabarit calculée. */
const SCOPE_LABEL_KEYS = {
  organization: 'acceptInvitation.scope.organization',
  team: 'acceptInvitation.scope.team',
  project: 'acceptInvitation.scope.project',
};

/** Laisse lire « Invitation acceptée » avant d'ouvrir la destination. */
const REDIRECT_DELAY_MS = 800;

/** Aperçu public ; sur 404 et si la personne est connectée, son éventuel statut « déjà rejoint ». */
async function chargerInvitation(token, connecte, signal) {
  try {
    const { data } = await api.get(`/invitations/${token}`, { signal, auth: false });
    return { kind: 'apercu', apercu: data };
  } catch (err) {
    if (err?.status !== 404 || !connecte) throw err;
    try {
      const { data } = await api.get(`/invitations/${token}/statut`, { signal });
      return { kind: 'rejoint', statut: data };
    } catch {
      // 404 indistinct côté serveur : rien à prouver, le message générique s'applique.
      throw err;
    }
  }
}

/** Refus d'acceptation qui tiennent au compte connecté : proposer de se déconnecter. */
const CODES_REFUS_COMPTE = new Set([
  'MSAL_REQUIS',
  'INVITATION_EMAIL_MISMATCH',
  'EMAIL_NOT_VERIFIED',
]);

/** Mode `registre` : acceptation par POST, jeton dans le corps, sans aperçu (#168). */
function AcceptationRegistre({ token }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, user, logout } = useSession();
  const connecte = status === 'authenticated';
  /** `{ id, nom }` de l'organisation une fois l'invitation acceptée. */
  const [accepted, setAccepted] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [refusCompte, setRefusCompte] = useState(false);

  // Repli local (#172) : une invitation d'équipe/projet répond `{ destination }` ; celle du
  // registre répond `{ organisation }`.
  const redirection = accepted
    ? cheminDeDestination(
        accepted.destination ?? { scopeType: 'organization', organizationId: accepted.id },
      )
    : null;
  useEffect(() => {
    if (!redirection) return undefined;
    const timer = setTimeout(() => navigate(redirection, { replace: true }), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [redirection, navigate]);

  const acceptNow = async () => {
    setAccepting(true);
    setAcceptError(null);
    setRefusCompte(false);
    try {
      const { data } = await api.post('/invitations/accepter', { jeton: token });
      setAccepted({
        id: data?.organisation?.id ?? null,
        nom: data?.organisation?.nom ?? null,
        destination: data?.destination ?? null,
      });
    } catch (err) {
      if (err?.status === 404) {
        // Message inchangé : le registre ne distingue ni révoquée, ni expirée, ni inconnue.
        setIntrouvable(true);
      } else {
        // Refus lié au COMPTE connecté : se reconnecter avec le bon compte est la seule issue.
        setRefusCompte(CODES_REFUS_COMPTE.has(err?.details?.code));
        setAcceptError(
          messageInvitation(err, t, { email: user?.email }) ??
            messageModeleOrganisation(err, t) ??
            t('acceptInvitation.acceptFailed', { message: err.message }),
        );
      }
    } finally {
      setAccepting(false);
    }
  };

  if (introuvable) {
    return (
      <Shell>
        <div className="p-6">
          <Empty>{t('acceptInvitation.notFound')}</Empty>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mx-auto mt-10 w-[440px] max-w-full p-4">
        <div className="relative border-2 border-line bg-paper p-6">
          <span className="ticks" aria-hidden="true" />
          <p className={EYEBROW}>{t('acceptInvitation.eyebrow')}</p>
          <h1 className={PAGE_TITLE}>{t('acceptInvitation.registre.title')}</h1>
          <p className="mt-4 text-sm text-ink-soft">{t('acceptInvitation.registre.message')}</p>

          {accepted ? (
            <p role="status" className="mt-6 text-sm text-ink">
              {accepted.nom
                ? t('acceptInvitation.registre.acceptedRedirect', { name: accepted.nom })
                : t('acceptInvitation.registre.acceptedNoName')}
            </p>
          ) : connecte ? (
            <div className="mt-6">
              {acceptError && (
                <p role="alert" className="mb-3 text-sm text-accent-ink">
                  {acceptError}
                </p>
              )}
              <PrimaryButton disabled={accepting} onClick={acceptNow}>
                {t('acceptInvitation.accept')}
              </PrimaryButton>
              {refusCompte && (
                <button type="button" className={`${BTN_GHOST} ml-2`} onClick={logout}>
                  {t('invitationErrors.logout')}
                </button>
              )}
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-2">
              <p className="text-sm text-ink-soft">{t('acceptInvitation.connectPrompt')}</p>
              {/* Pas de connexion legacy en mode `registre` : le jeton passerait dans `returnTo` et
                  l'acceptation exige de toute façon une connexion Microsoft (403 MSAL_REQUIS). */}
              {!ciamConfigured && (
                <p className="text-sm text-ink-soft">{t('orgErrors.msalRequis')}</p>
              )}
              <button
                type="button"
                className={`${BTN_DARK} w-full`}
                disabled={!ciamConfigured}
                onClick={() => startLogin(`/invitation/${token}`)}
              >
                {t('login.action')}
              </button>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

/** Aiguillage par mode : deux composants distincts, aucun hook conditionnel. */
export function AcceptInvitationPage() {
  const { token } = useParams();
  const { organisationStudio } = useSession();
  return organisationStudio === 'registre' ? (
    <AcceptationRegistre token={token} />
  ) : (
    <AcceptationLocale token={token} />
  );
}

/** Mode `local` : aperçu public puis acceptation (comportement d'avant le lot O3). */
function AcceptationLocale({ token }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, providers, user, logout } = useSession();
  const principal = fournisseurPrincipal(providers);
  const connecte = status === 'authenticated';
  /** `{ destination, alreadyAccepted }` une fois l'invitation acceptée par cette personne. */
  const [accepted, setAccepted] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [mismatch, setMismatch] = useState(false);

  const {
    data: charge,
    error,
    loading,
  } = useAsync((signal) => chargerInvitation(token, connecte, signal), [token, connecte]);

  const redirection = accepted && !accepted.alreadyAccepted ? accepted.destination : null;
  useEffect(() => {
    if (!redirection) return undefined;
    const timer = setTimeout(
      () => navigate(cheminDeDestination(redirection), { replace: true }),
      REDIRECT_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [redirection, navigate]);

  const acceptNow = async () => {
    setAccepting(true);
    setAcceptError(null);
    try {
      const { data: reponse } = await api.post(`/invitations/${token}/accept`);
      setAccepted({
        alreadyAccepted: Boolean(reponse?.alreadyAccepted),
        destination: reponse?.destination,
      });
    } catch (err) {
      setMismatch(err?.details?.code === 'INVITATION_EMAIL_MISMATCH');
      // Le message d'invitation d'abord : `EMAIL_NOT_VERIFIED` existe aussi dans `org-errors`,
      // mais formulé pour l'administrateur qui approuve une demande, pas pour l'invité.
      setAcceptError(
        messageInvitation(err, t, { email: user?.email }) ??
          messageModeleOrganisation(err, t) ??
          t('acceptInvitation.acceptFailed', { message: err.message }),
      );
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <p className="p-6 text-sm text-ink-soft">{t('acceptInvitation.loading')}</p>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="p-6">
          <Empty>{t('acceptInvitation.notFound')}</Empty>
        </div>
      </Shell>
    );
  }

  const data = charge.kind === 'rejoint' ? charge.statut : charge.apercu;
  const scopeLabelKey = SCOPE_LABEL_KEYS[data.scopeType];
  const scope = scopeLabelKey ? t(scopeLabelKey, { name: data.scopeLabel }) : data.scopeLabel;

  // Déjà rejoint : par le statut du serveur, ou parce que l'acceptation vient de répondre ainsi.
  if (charge.kind === 'rejoint' || accepted?.alreadyAccepted) {
    const destination = charge.kind === 'rejoint' ? data.destination : accepted.destination;
    return (
      <Shell>
        <div className="mx-auto mt-10 w-[440px] max-w-full p-4">
          <div className="relative border-2 border-line bg-paper p-6">
            <span className="ticks" aria-hidden="true" />
            <p className={EYEBROW}>{t('acceptInvitation.eyebrow')}</p>
            <h1 className={PAGE_TITLE}>{data.organizationName}</h1>
            <p role="status" className="mt-4 text-sm text-ink">
              {t('acceptInvitation.alreadyJoined', { scope })}
            </p>
            {/* Écran fermé, sans en-tête de page : son action unique reste LE primaire. */}
            <div className="mt-6">
              <PrimaryButton onClick={() => navigate(cheminDeDestination(destination))}>
                {t('acceptInvitation.open')}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mx-auto mt-10 w-[440px] max-w-full p-4">
        <div className="relative border-2 border-line bg-paper p-6">
          <span className="ticks" aria-hidden="true" />
          <p className={EYEBROW}>{t('acceptInvitation.eyebrow')}</p>
          <h1 className={PAGE_TITLE}>{data.organizationName}</h1>

          <p className="mt-4 text-sm text-ink-soft">
            {data.inviterName
              ? t('acceptInvitation.invitedByAndScope', { inviter: data.inviterName, scope })
              : t('acceptInvitation.scopeOnly', { scope })}
          </p>

          <div className="mt-3">
            <RoleBadge role={data.role} />
          </div>

          {accepted ? (
            <p role="status" className="mt-6 text-sm text-ink">
              {t('acceptInvitation.acceptedRedirect', { scope })}
            </p>
          ) : connecte ? (
            <div className="mt-6">
              {acceptError && (
                <p role="alert" className="mb-3 text-sm text-accent-ink">
                  {acceptError}
                </p>
              )}
              {/* Écran fermé, sans en-tête de page : son action unique reste LE primaire
                  (`PrimaryButton`, common.jsx). */}
              <PrimaryButton disabled={accepting} onClick={acceptNow}>
                {t('acceptInvitation.accept')}
              </PrimaryButton>
              {mismatch && (
                <button type="button" className={`${BTN_GHOST} ml-2`} onClick={logout}>
                  {t('invitationErrors.logout')}
                </button>
              )}
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-2">
              <p className="text-sm text-ink-soft">{t('acceptInvitation.connectPrompt')}</p>
              {/* Un seul bouton (gabarit exiUI) : le retour se fait sur CETTE page, l'invité
                  doit atterrir sur l'acceptation, pas sur la liste des projets. */}
              {(ciamConfigured || principal) && (
                <button
                  type="button"
                  className={`${BTN_DARK} w-full`}
                  onClick={() =>
                    ciamConfigured
                      ? startLogin(`/invitation/${token}`)
                      : startLegacyLogin(principal, `/invitation/${token}`)
                  }
                >
                  {t('login.action')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-paper">
      <header className="h-11 border-b-2 border-t-2 border-line flex items-center justify-between px-3">
        <span className={BRAND}>
          <span className={BRAND_GLYPH} aria-hidden="true">
            <GlypheExiPlan />
          </span>
          exiPlan
        </span>
        <MenuLangue />
      </header>
      {children}
    </div>
  );
}
